/**
 * 저장 결과에 따라 부분 갱신을 처리하는 훅
 * 
 * 성공한 데이터는 재조회로 갱신하고, 실패한 데이터는 그리드에 그대로 유지합니다.
 * 
 * @param {Object} options
 * @param {Object} options.gridRef - 그리드 ref
 * @param {Array} options.changedRowData - 변경된 행 데이터 배열
 * @param {Function} options.setChangedRowData - 변경된 행 데이터 설정 함수
 * @param {Function} options.setRowData - 전체 행 데이터 설정 함수
 * @param {Function} options.getKeyFromRow - 행에서 키를 추출하는 함수 (예: (row) => row.employeeNo)
 * @param {Function} options.getKeyFromResult - 결과에서 키를 추출하는 함수 (예: (result) => result.employeeNo)
 * 
 * @returns {Function} handlePartialRefresh - 부분 갱신 처리 함수
 * 
 * @example
 * const { handlePartialRefresh } = usePartialRefresh({
 *   gridRef: gridRef1,
 *   changedRowData: changedRowData1,
 *   setChangedRowData: setChangedRowData1,
 *   setRowData: setRowData1,
 *   getKeyFromRow: (row) => row.employeeNo,
 *   getKeyFromResult: (result) => result.employeeNo,
 * });
 * 
 * // 저장 후 호출
 * await handlePartialRefresh({
 *   saveResult: saveDetailData, // 저장 결과 배열
 *   changedRows: changedRows, // 저장한 행 데이터 배열
 *   searchLogic: SearchLogic1, // 재조회 함수
 * });
 */
export default function usePartialRefresh({
  gridRef,
  changedRowData = [],
  setChangedRowData,
  setRowData,
  getKeyFromRow,
  getKeyFromResult,
}) {
  const toKey = (rowOrResult) => {
    const key = getKeyFromRow?.(rowOrResult);
    if (key === null || key === undefined) return null;
    return String(key);
  };

  /**
   * 부분 갱신 처리 함수
   * 
   * @param {Object} options
   * @param {Array} options.saveResult - 저장 결과 배열 (각 항목은 isSuccess 필드를 가짐)
   * @param {Array} options.changedRows - 저장한 행 데이터 배열
   * @param {Function} options.searchLogic - 재조회 함수
   */
  const handlePartialRefresh = async ({ saveResult = [], changedRows = [], searchLogic }) => {
    if (!searchLogic) {
      console.error('usePartialRefresh: searchLogic이 필요합니다.');
      return;
    }

    if (!saveResult || saveResult.length === 0) {
      // 저장 결과가 없으면 전체 재조회
      await searchLogic();
      return;
    }

    // saveResult와 changedRows는 같은 순서로 매칭됨
    // 인덱스를 기반으로 성공/실패 행을 구분
    const successIndices = new Set();
    const failIndices = new Set();

    saveResult.forEach((result, index) => {
      if (result.isSuccess === false) {
        failIndices.add(index);
      } else {
        successIndices.add(index);
      }
    });

    // 성공한 행 데이터 추출 (changedRows에서)
    const successRows = changedRows.filter((row, index) => successIndices.has(index));

    // 실패한 행 데이터 추출 (changedRows에서)
    const failRows = changedRows.filter((row, index) => failIndices.has(index));

    // 성공한 데이터의 키 추출 (changedRows에서)
    const successKeys = new Set(
      successRows.map((row) => {
        return toKey(row);
      }).filter((key) => key !== null)
    );

    // 실패한 데이터의 키 추출 (changedRows에서)
    const failKeys = new Set(
      failRows.map((row) => {
        return toKey(row);
      }).filter((key) => key !== null)
    );

    // 그리드에서 성공한 데이터 제거
    if (gridRef?.current?.api && successKeys.size > 0) {
      const nodesToRemove = [];
      gridRef.current.api.forEachNode((node) => {
        const rowKey = toKey(node.data);
        if (rowKey !== null && successKeys.has(rowKey)) {
          nodesToRemove.push(node);
        }
      });

      if (nodesToRemove.length > 0) {
        gridRef.current.api.applyTransaction({ remove: nodesToRemove.map((n) => n.data) });
      }
    }

    // 재조회 실행
    await searchLogic();

    // changedRowData를 함수형 업데이트로 수정 (기존 순서 유지)
    setChangedRowData((current) => {
      if (!Array.isArray(current)) return [];

      // 실패한 행의 키를 Map으로 변환 (빠른 조회를 위해)
      const failRowsMap = new Map();
      failRows.forEach((failRow) => {
        const key = toKey(failRow);
        if (key !== null) {
          failRowsMap.set(key, failRow);
        }
      });

      // 기존 changedRowData를 순회하면서 업데이트
      // 1. 성공한 행은 제거
      // 2. 실패한 행은 업데이트 (failRows의 최신 데이터로)
      // 3. 나머지는 그대로 유지
      const updated = current
        .map((row) => {
          const keyStr = toKey(row);
          if (keyStr === null) return row;

          // 성공한 행은 제거 (null 반환 후 필터링)
          if (successKeys.has(keyStr)) {
            return null;
          }

          // 실패한 행은 최신 데이터로 업데이트
          if (failRowsMap.has(keyStr)) {
            return failRowsMap.get(keyStr);
          }

          // 나머지는 그대로 유지
          return row;
        })
        .filter((row) => row !== null); // null 제거

      // failRows에 있지만 current에 없는 행 추가 (새로 추가된 실패 행)
      failRows.forEach((failRow) => {
        const failKeyStr = toKey(failRow);
        if (failKeyStr === null) return;
        const exists = updated.some((row) => {
          const rowKey = toKey(row);
          return rowKey !== null && rowKey === failKeyStr;
        });

        if (!exists) {
          updated.push(failRow);
        }
      });

      return updated;
    });

    // 재조회 후 그리드/rowData에 실패한 데이터 다시 추가 (재조회 결과에 없으면)
    if (gridRef?.current?.api && failRows.length > 0) {
      setTimeout(() => {
        const currentGridData = [];
        gridRef.current.api.forEachNode((node) => {
          currentGridData.push(node.data);
        });

        // 재조회 결과에 없는 실패한 행만 추가
        const rowsToAdd = failRows.filter((failRow) => {
          const failKeyStr = toKey(failRow);
          if (failKeyStr === null) return false;

          const existsInGrid = currentGridData.some((gridRow) => {
            const gridKeyStr = toKey(gridRow);
            return gridKeyStr !== null && gridKeyStr === failKeyStr;
          });

          return !existsInGrid;
        });

        if (rowsToAdd.length > 0) {
          gridRef.current.api.applyTransaction({ add: rowsToAdd });
        }

        // ✅ rowData(React state)도 함께 보정: rowData 기반 렌더링이라서,
        // grid API transaction만으로는 다음 렌더에서 실패 행이 사라질 수 있음.
        if (typeof setRowData === 'function') {
          setRowData(() => {
            const base = Array.isArray(currentGridData) ? currentGridData : [];
            if (!rowsToAdd.length) return base;

            const baseKeys = new Set(base.map((r) => toKey(r)).filter((k) => k !== null));
            const merged = [...base];
            rowsToAdd.forEach((r) => {
              const k = toKey(r);
              if (k === null) {
                merged.push(r);
                return;
              }
              if (!baseKeys.has(k)) {
                baseKeys.add(k);
                merged.push(r);
              }
            });
            return merged;
          });
        }
      }, 100); // 재조회 완료 대기
    }
  };

  return { handlePartialRefresh };
}

